import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  CloudDownload,
  Download,
  Globe,
  PackagePlus,
  Search,
  Store,
  TrendingUp,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Send } from "lucide-react";

import { PushPriceModal } from "@/components/PushPriceModal";
import { SelectableProductsTable } from "@/components/ProductsTable";
import {
  api,
  type ImportReport,
  type MarketplaceAutomation,
  type MarketplaceOut,
  type ProductOut,
} from "@/lib/api";
import { FILTRI_OFFERTA, filtroValido, type FiltroProdotti } from "@/lib/filtriOfferta";
import { riepilogoMotivi } from "@/lib/motiviBlocco";
import { cn } from "@/lib/utils";

const PRODUCTS_PAGE_VIEW_STORAGE_KEY = "analisi-marketplace.products.page-view.v1";

interface ProductsPageView {
  country: string;
  marketplaceId: string;
}

export function Products(): JSX.Element {
  const qc = useQueryClient();
  const marketplaces = useQuery({
    queryKey: ["marketplaces"],
    queryFn: api.marketplaces,
  });
  const automation = useQuery<MarketplaceAutomation[]>({
    queryKey: ["marketplaces-automation"],
    queryFn: api.marketplaceAutomation,
  });
  const marketplaceOptions = marketplaces.data ?? DEFAULT_MARKETPLACES;
  const savedView = useMemo(() => loadProductsPageView(), []);
  // Link dalla Panoramica: `?canale=` sceglie il canale, `?filtro=` il sottoinsieme.
  const [params, setParams] = useSearchParams();
  const canaleUrl = params.get("canale");
  const filtro = filtroValido(params.get("filtro"));
  const [country, setCountry] = useState(savedView.country);
  const [marketplaceId, setMarketplaceId] = useState(canaleUrl ?? savedView.marketplaceId);
  const countries = useMemo(
    () =>
      Array.from(new Set(marketplaceOptions.map((m) => m.country))).sort(
        (a, b) => countryOrder(a) - countryOrder(b),
      ),
    [marketplaceOptions],
  );
  const filteredMarketplaces = useMemo(
    () => marketplaceOptions.filter((m) => m.country === country),
    [country, marketplaceOptions],
  );

  useEffect(() => {
    if (!canaleUrl || !marketplaces.data) return;
    const canale = marketplaces.data.find((m) => m.id === canaleUrl);
    if (!canale) return;
    setCountry(canale.country);
    setMarketplaceId(canale.id);
  }, [canaleUrl, marketplaces.data]);

  const toggleFiltro = (valore: FiltroProdotti) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get("filtro") === valore) next.delete("filtro");
      else next.set("filtro", valore);
      return next;
    });
  };

  // Una scelta a mano del canale o del paese vince sul canale arrivato dal link.
  const dimenticaCanaleUrl = () => {
    if (!canaleUrl) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("canale");
        return next;
      },
      { replace: true },
    );
  };

  const list = useQuery({
    queryKey: ["products", marketplaceId],
    queryFn: () => api.listProducts({ marketplace_id: marketplaceId }),
    staleTime: 0,
    refetchOnMount: "always",
  });
  const [search, setSearch] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<ProductOut[]>([]);
  const [pushOpen, setPushOpen] = useState<boolean>(false);
  const [exportingOffers, setExportingOffers] = useState(false);
  const [offersExportError, setOffersExportError] = useState<string | null>(null);
  const [exportingMissing, setExportingMissing] = useState(false);
  const [missingExportError, setMissingExportError] = useState<string | null>(null);

  const importer = useMutation({
    mutationFn: (file: File) => api.importProducts(file),
    onSuccess: (r) => {
      setReport(r);
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });

  const [poleepoImportFlash, setPoleepoImportFlash] = useState<
    "idle" | "started" | "conflict" | "error"
  >("idle");

  const poleepoImporter = useMutation({
    mutationFn: () => api.importPoleepoCatalog(),
    onSuccess: () => {
      // Invalidiamo la query products: lo stream di completamento
      // arriva via WebSocket nel Layout watchdog; quando l'utente
      // torna sulla pagina vedra' i nuovi prodotti.
      qc.invalidateQueries({ queryKey: ["products"] });
      setPoleepoImportFlash("started");
      // Reset del flash dopo 8s: il bottone torna abilitato cosi'
      // l'utente puo' rifare il sync (entro il lock 409 il server
      // protegge da double-trigger).
      window.setTimeout(() => setPoleepoImportFlash("idle"), 8000);
    },
    onError: (err: Error) => {
      // Lock attivo lato server -> 409: distinguiamo per UX.
      setPoleepoImportFlash(/409/.test(err.message) ? "conflict" : "error");
      window.setTimeout(() => setPoleepoImportFlash("idle"), 5000);
    },
  });

  useEffect(() => {
    // Il canale del link non si scarta: gli effetti girano nello stesso ciclo e il
    // riallineamento vedrebbe ancora il paese salvato, non quello del canale.
    if (
      canaleUrl &&
      marketplaceId === canaleUrl &&
      (!marketplaces.data || marketplaces.data.some((m) => m.id === canaleUrl))
    )
      return;
    if (filteredMarketplaces.length === 0) return;
    if (!filteredMarketplaces.some((m) => m.id === marketplaceId)) {
      setMarketplaceId(filteredMarketplaces[0].id);
    }
  }, [filteredMarketplaces, marketplaceId, canaleUrl, marketplaces.data]);

  useEffect(() => {
    saveProductsPageView({ country, marketplaceId });
  }, [country, marketplaceId]);

  // Cambio marketplace -> la selezione precedente non e' piu' valida
  // (gli SKU appartengono a un altro marketplace) e i banner di
  // success/error puntano a un export precedente. Resettiamo tutto per
  // evitare che "Offerte" o "Offerte nuove" inviino SKU al marketplace
  // sbagliato.
  useEffect(() => {
    setSelectedProducts([]);
    setOffersExportError(null);
    setMissingExportError(null);
  }, [marketplaceId]);

  const visibleProducts = useMemo(() => {
    let all = list.data ?? [];
    if (search) {
      const q = search.toLowerCase();
      all = all.filter(
        (p) =>
          p.sku.toLowerCase().includes(q) ||
          (p.ean ?? "").toLowerCase().includes(q) ||
          (p.brand ?? "").toLowerCase().includes(q) ||
          (p.name ?? "").toLowerCase().includes(q),
      );
    }
    return filtro ? all.filter(FILTRI_OFFERTA[filtro].test) : all;
  }, [list.data, filtro, search]);

  // Contatori dalla classificazione del backend: gli stessi numeri della Panoramica.
  const counts = useMemo(() => {
    const righe = list.data ?? [];
    const conta = (test: (p: ProductOut) => boolean) => righe.filter(test).length;
    const recoverableBlocked: Record<string, number> = {};
    for (const p of righe) {
      const r = p.recoverable_block_reason;
      if (r && r !== "actionable") recoverableBlocked[r] = (recoverableBlocked[r] ?? 0) + 1;
    }
    return {
      total: righe.length,
      inbox: conta(FILTRI_OFFERTA.vinte.test),
      out: conta(
        (p) => FILTRI_OFFERTA.recuperabili.test(p) || FILTRI_OFFERTA.al_limite.test(p) || FILTRI_OFFERTA.perse_altro.test(p),
      ),
      recoverable: conta(FILTRI_OFFERTA.recuperabili.test),
      recoverableActionable: conta((p) => p.recoverable_block_reason === "actionable"),
      recoverableBlocked,
      missing: conta(FILTRI_OFFERTA.opportunita.test),
      belowMargin: conta(FILTRI_OFFERTA.sotto_margine.test),
    };
  }, [list.data]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) importer.mutate(file);
    e.target.value = "";
  };

  const downloadTemplate = async (fmt: "csv" | "xlsx") => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await fetch(api.templateUrl(fmt));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "csv" ? "modello_prodotti.csv" : "modello_prodotti.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  };

  const exportMarketplaceOffers = async () => {
    const skus = selectedProducts.map((product) => product.sku);
    if (skus.length === 0) return;
    setExportingOffers(true);
    setOffersExportError(null);
    try {
      const { blob, filename } = await api.exportMarketplaceOffers(marketplaceId, skus);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        filename ?? `${marketplaceId}_recuperabili_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setOffersExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExportingOffers(false);
    }
  };

  const exportMarketplaceMissing = async () => {
    const skus = selectedProducts.map((product) => product.sku);
    if (skus.length === 0) return;
    setExportingMissing(true);
    setMissingExportError(null);
    try {
      const { blob, filename } = await api.exportMarketplaceMissing(marketplaceId, skus);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        filename ?? `${marketplaceId}_offerte_nuove_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMissingExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExportingMissing(false);
    }
  };

  const selectedMarketplaceLabel =
    filteredMarketplaces.find((m) => m.id === marketplaceId)?.label ?? marketplaceId;
  const totalProducts = list.data?.length ?? 0;

  return (
    <>
      {/* Page header: title + counter + paese/marketplace selectors a destra */}
      <div className="px-7 pb-4 pt-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex min-w-0 items-baseline gap-3">
            <h1 className="page-title">Prodotti</h1>
            <span className="text-md text-ink-3">
              {list.isLoading && "caricamento…"}
              {list.isError && "errore caricamento"}
              {list.data && (
                <>
                  {visibleProducts.length}/{totalProducts} su{" "}
                  <span className="font-medium text-ink">{selectedMarketplaceLabel}</span>
                </>
              )}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ChipSelector
              icon={Globe}
              label="Paese"
              value={country}
              options={countries.map((c) => ({ value: c, label: c }))}
              onChange={(v) => {
                dimenticaCanaleUrl();
                setCountry(v);
              }}
            />
            <ChipSelector
              icon={Store}
              label="Marketplace"
              value={marketplaceId}
              options={filteredMarketplaces.map((m) => ({ value: m.id, label: m.label }))}
              onChange={(v) => {
                dimenticaCanaleUrl();
                setMarketplaceId(v);
              }}
            />
          </div>
        </div>
      </div>

      <div className="space-y-3 px-7 pb-6">
        {/* KPI strip inline */}
        <KpiStrip counts={counts} />

        {/* Toolbar */}
        <div className="card-stack divide-y divide-line">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
          <SearchBar value={search} onChange={setSearch} />
          <button
            type="button"
            onClick={() => toggleFiltro("recuperabili")}
            aria-pressed={filtro === "recuperabili"}
            title="Mostra solo le offerte che recuperano la buybox a -1 EUR rispettando il margine minimo"
            className={cn("btn btn-sm", filtro === "recuperabili" && "toggle-on")}
          >
            <TrendingUp className="ic" strokeWidth={1.6} aria-hidden="true" />
            Recuperabili
            <span className="count">{counts.recoverable}</span>
          </button>
          <button
            type="button"
            onClick={() => toggleFiltro("opportunita")}
            aria-pressed={filtro === "opportunita"}
            title="Mostra solo i prodotti non venduti su questo canale dove concorrente -1 EUR rispetta il margine minimo"
            className={cn("btn btn-sm", filtro === "opportunita" && "toggle-warn-on")}
          >
            <PackagePlus className="ic" strokeWidth={1.6} aria-hidden="true" />
            Opportunità
            <span className="count">{counts.missing}</span>
          </button>
          <button
            type="button"
            onClick={() => toggleFiltro("sotto_margine")}
            aria-pressed={filtro === "sotto_margine"}
            title="Mostra solo le offerte il cui prezzo di vendita e' sotto il floor di margine"
            className={cn("btn btn-sm", filtro === "sotto_margine" && "toggle-warn-on")}
          >
            <AlertTriangle className="ic" strokeWidth={1.6} aria-hidden="true" />
            Sotto margine
            <span className="count">{counts.belowMargin}</span>
          </button>
          {filtro && filtro !== "recuperabili" && filtro !== "opportunita" && filtro !== "sotto_margine" ? (
            <button
              type="button"
              onClick={() => toggleFiltro(filtro)}
              aria-pressed="true"
              title="Filtro arrivato dalla Panoramica: clic per toglierlo"
              className="btn btn-sm toggle-on"
            >
              {FILTRI_OFFERTA[filtro].label}
              <X className="ic" strokeWidth={1.6} aria-hidden="true" />
            </button>
          ) : null}

          </div>
          <div className="flex flex-wrap items-center gap-2 px-3 py-2">

          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx"
            className="hidden"
            onChange={onFileChange}
          />
          <button
            type="button"
            onClick={() => poleepoImporter.mutate()}
            disabled={poleepoImporter.isPending || poleepoImportFlash === "started"}
            className="btn btn-sm"
            title="Importa anagrafica (SKU/EAN/nome/brand/immagine) da Poleepo. Preserva costi/margini gia' impostati. Durata 30-60s."
          >
            <CloudDownload className="ic" strokeWidth={1.6} aria-hidden="true" />
            {poleepoImporter.isPending
              ? "Avvio…"
              : poleepoImportFlash === "started"
                ? "Avviato — vedi Storico"
                : poleepoImportFlash === "conflict"
                  ? "Già in corso…"
                  : poleepoImportFlash === "error"
                    ? "Errore, riprova"
                    : "Da Poleepo"}
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={importer.isPending}
            className="btn btn-sm"
            title="Carica un file CSV/XLSX di prodotti"
          >
            <Upload className="ic" strokeWidth={1.6} />
            {importer.isPending ? "Caricamento…" : "Importa"}
          </button>
          <button
            type="button"
            onClick={() => downloadTemplate("xlsx")}
            disabled={downloading}
            className="btn btn-sm"
            title="Scarica il modello XLSX vuoto da compilare per l'import"
          >
            <Download className="ic" strokeWidth={1.6} aria-hidden="true" />
            {downloading ? "Salvataggio…" : "Modello import"}
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={exportMarketplaceOffers}
            disabled={selectedProducts.length === 0 || exportingOffers}
            className="btn btn-sm"
            title="CSV UTF-8 con SKU, EAN, Prezzo, PREZZO OFFERTA, PERCENTUALE per la selezione (offerte gia attive)"
          >
            <Download className="ic" strokeWidth={1.6} aria-hidden="true" />
            {exportingOffers ? "Export…" : `Offerte esistenti (${selectedProducts.length})`}
          </button>
          <button
            type="button"
            onClick={exportMarketplaceMissing}
            disabled={selectedProducts.length === 0 || exportingMissing}
            className="btn btn-sm"
            title="CSV UTF-8 per caricare nuove offerte su SKU oggi assenti dal marketplace"
          >
            <PackagePlus className="ic" strokeWidth={1.6} aria-hidden="true" />
            {exportingMissing ? "Export…" : `Offerte mancanti (${selectedProducts.length})`}
          </button>
          {(() => {
            const single = selectedProducts.length === 1 ? selectedProducts[0] : null;
            const singleMarketId = single?.marketplace_id ?? marketplaceId;
            const singleAutoRow = single
              ? automation.data?.find((row) => row.id === singleMarketId)
              : null;
            const autoOn = singleAutoRow?.auto_apply ?? false;
            const hasSuggested = Boolean(single?.buybox_target_price);
            const canPush = Boolean(single) && !autoOn && hasSuggested;
            const tooltip = !single
              ? "Seleziona 1 prodotto in tabella"
              : selectedProducts.length > 1
                ? "Seleziona un solo prodotto per inviare il prezzo"
                : autoOn
                  ? "Auto-apply attivo per questo marketplace; disattivalo prima del push manuale"
                  : !hasSuggested
                    ? "Nessun suggerimento (esegui un refresh marketplace)"
                    : `Push ${single?.buybox_target_price}€ su ${single?.marketplace_label ?? singleMarketId}`;
            return (
              <button
                type="button"
                disabled={!canPush}
                onClick={() => setPushOpen(true)}
                className={cn(
                  "btn btn-sm",
                  canPush ? "btn-accent" : "",
                )}
                title={tooltip}
                aria-label="Invia prezzo a Poleepo e Mirakl"
              >
                <Send className="ic" strokeWidth={1.6} aria-hidden="true" />
                {single
                  ? `Push prezzo${hasSuggested ? ` ${single.buybox_target_price}€` : ""}`
                  : "Push prezzo"}
              </button>
            );
          })()}
          </div>
        </div>

        {(() => {
          if (!pushOpen || selectedProducts.length !== 1) return null;
          const product = selectedProducts[0];
          const productMarketId = product.marketplace_id ?? marketplaceId;
          const autoRow = automation.data?.find((row) => row.id === productMarketId);
          if (!product.buybox_target_price) return null;
          return (
            <PushPriceModal
              sku={product.sku}
              marketplaceId={productMarketId}
              marketplaceLabel={
                product.marketplace_label ?? autoRow?.label ?? productMarketId
              }
              myPrice={product.my_price}
              myDefaultPrice={product.my_default_price}
              suggestedPrice={product.buybox_target_price}
              expectedMarginPct={product.buybox_target_margin_percent}
              onClose={() => setPushOpen(false)}
              onSuccess={(status) => {
                setPushOpen(false);
                qc.invalidateQueries({ queryKey: ["products"] });
                qc.invalidateQueries({ queryKey: ["activity"] });
                setSelectedProducts([]);
                if (typeof window !== "undefined") {
                  const msg =
                    status === "ok"
                      ? "Prezzo inviato a Poleepo + Mirakl"
                      : status === "mirakl_only"
                        ? "Prezzo inviato direttamente a Mirakl (questo SKU non passa da Poleepo)"
                        : "Poleepo aggiornato (Mirakl in sync ritardata)";
                  // Toast usa lo stesso "kind" per il colore: ok/mirakl_only
                  // sono entrambi success (sage), partial e' warn (ocra).
                  const kind = status === "partial" ? "partial" : "ok";
                  window.dispatchEvent(
                    new CustomEvent("am-toast", { detail: { kind, msg } }),
                  );
                }
              }}
            />
          );
        })()}

      {downloadError && (
        <div className="rounded-[var(--radius-lg)] border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Errore nel download del modello: {downloadError}
        </div>
      )}

      {offersExportError && (
        <div className="rounded-[var(--radius-lg)] border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Errore export offerte: {offersExportError}
        </div>
      )}

      {missingExportError && (
        <div className="rounded-[var(--radius-lg)] border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Errore export offerte nuove: {missingExportError}
        </div>
      )}

      {report && (
        <div className="panel px-4 py-3 text-sm">
          Importati <strong>{report.imported}</strong> - scartati <strong>{report.skipped}</strong>
          {report.skipped > 0 && (
            <details className="mt-2 text-xs text-muted-foreground">
              <summary>Errori di validazione</summary>
              <ul className="mt-1 list-disc pl-4">
                {report.rows
                  .filter((r) => !r.accepted)
                  .map((r) => (
                    <li key={`${r.row}-${r.sku}`}>
                      Riga {r.row} ({r.sku || "-"}/{r.ean || "-"}): {r.error}
                    </li>
                  ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {list.isError && (
        <div className="rounded-[var(--radius-lg)] border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Errore caricamento prodotti:{" "}
          {list.error instanceof Error ? list.error.message : String(list.error)}
        </div>
      )}

      {list.isLoading ? (
        <ProductsLoading />
      ) : (
        <SelectableProductsTable
          products={visibleProducts}
          onSelectionChange={setSelectedProducts}
        />
      )}
      </div>
    </>
  );
}

function ProductsLoading(): JSX.Element {
  return (
    <div className="card-stack overflow-hidden">
      <div className="grid min-w-[2560px] grid-cols-[repeat(17,minmax(0,1fr))] gap-4 border-b border-line bg-paper px-3 py-3">
        {Array.from({ length: 17 }, (_, index) => (
          <div key={index} className="h-3 w-20 animate-pulse rounded bg-paper-2" />
        ))}
      </div>
      <div className="space-y-2 p-3">
        {Array.from({ length: 8 }, (_, index) => (
          <div key={index} className="h-8 animate-pulse rounded-lg bg-paper-2" />
        ))}
      </div>
    </div>
  );
}

interface KpiCounts {
  total: number;
  inbox: number;
  out: number;
  recoverable: number;
  recoverableActionable: number;
  recoverableBlocked: Record<string, number>;
  missing: number;
  belowMargin: number;
}

type KpiDot = "ok" | "err" | "info" | "neutral";

function KpiStrip({ counts }: { counts: KpiCounts }): JSX.Element {
  const tiles: Array<{
    label: string;
    value: number;
    dot: KpiDot;
    sub?: string;
    tooltip?: string;
  }> = [
    { label: "Monitorati", value: counts.total, dot: "neutral" },
    { label: "In buybox", value: counts.inbox, dot: "ok" },
    { label: "Buybox persa", value: counts.out, dot: "err" },
    {
      label: "Recuperabili",
      value: counts.recoverable,
      dot: "ok",
      sub: `${counts.recoverableActionable} in auto`,
      tooltip: riepilogoMotivi(counts.recoverableBlocked),
    },
    { label: "Opportunità", value: counts.missing, dot: "info" },
    { label: "Sotto margine", value: counts.belowMargin, dot: "err" },
  ];
  // Come la striscia della Panoramica: fogli bianchi separati da un filo.
  return (
    <div
      className="grid grid-cols-2 gap-px overflow-hidden rounded-[var(--radius-lg)] border border-line bg-line sm:grid-cols-3 lg:grid-cols-6"
      role="group"
      aria-label="KPI catalogo"
    >
      {tiles.map((t) => {
        const allarme = t.dot === "err" && t.value > 0;
        return (
          <div
            key={t.label}
            title={t.tooltip}
            className="flex min-w-0 flex-col gap-1 bg-card px-4 pb-3 pt-2.5"
          >
            <span className="flex items-center gap-1.5 truncate text-label text-ink-3">
              <span className={cn("dot", KPI_DOT[t.dot])} aria-hidden="true" />
              {t.label}
            </span>
            <span
              className={cn(
                "font-mono text-xl font-semibold leading-none tabular-nums",
                allarme ? "text-danger" : "text-ink",
              )}
            >
              {t.value}
            </span>
            {t.sub != null && (
              <span
                className={cn(
                  "text-micro tabular-nums",
                  counts.recoverableActionable > 0 ? "font-medium text-key" : "text-ink-3",
                )}
              >
                {t.sub}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const KPI_DOT: Record<KpiDot, string> = {
  ok: "dot-ok",
  err: "dot-err",
  info: "dot-info",
  neutral: "dot-neutral",
};

function SearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  return (
    <div className="relative min-w-[240px] max-w-md flex-1">
      <span
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-3"
        aria-hidden="true"
      >
        <Search className="h-4 w-4" strokeWidth={1.7} />
      </span>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label="Cerca prodotti"
        placeholder="Cerca per SKU, EAN, marchio o titolo…"
        className="h-9 w-full rounded-[var(--radius)] border border-line-2 bg-card pl-9 pr-3 text-md text-ink outline-none transition-colors focus:border-ink focus:bg-white"
      />
    </div>
  );
}

interface ChipSelectorOption {
  value: string;
  label: string;
}

function ChipSelector({
  icon: Icon,
  label,
  value,
  options,
  onChange,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  options: ChipSelectorOption[];
  onChange: (next: string) => void;
}): JSX.Element {
  return (
    <label className="btn btn-outline btn-sm cursor-pointer gap-2 px-3">
      <Icon className="ic" strokeWidth={1.7} aria-hidden="true" />
      <span className="text-label text-ink-3">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="cursor-pointer border-0 bg-transparent text-md font-semibold text-ink outline-none"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const DEFAULT_MARKETPLACES: MarketplaceOut[] = [
  {
    id: "maisons_du_monde",
    group: "maisons_du_monde",
    label: "Maisons du Monde IT",
    country: "IT",
    kind: "mirakl",
    enabled: true,
  },
];

function countryOrder(country: string): number {
  const order = ["IT", "ES", "FR"];
  const index = order.indexOf(country);
  return index === -1 ? order.length : index;
}

function loadProductsPageView(): ProductsPageView {
  const fallback: ProductsPageView = {
    country: "IT",
    marketplaceId: "maisons_du_monde",
  };
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(PRODUCTS_PAGE_VIEW_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as {
      country?: unknown;
      marketplaceId?: unknown;
    };
    return {
      country:
        typeof parsed.country === "string" && parsed.country ? parsed.country : fallback.country,
      marketplaceId:
        typeof parsed.marketplaceId === "string" && parsed.marketplaceId
          ? parsed.marketplaceId
          : fallback.marketplaceId,
    };
  } catch {
    return fallback;
  }
}

function saveProductsPageView(view: ProductsPageView): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PRODUCTS_PAGE_VIEW_STORAGE_KEY, JSON.stringify(view));
  } catch {
    // Page preferences are a convenience; keep the view usable even without local storage.
  }
}
